// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import type { TyLogSeverity, TySystemLogRow } from "@/types";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import StatsSection from "@/UI/components/StatsSection";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { formatDateTime, formatMs, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; errors: number; rejected: number; avgResponseTime: number };

// --- Constants ----------------------------------------------------------------------------------

const SEVERITIES: TyLogSeverity[] = ["success", "rejected", "error"];

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

// --- Logic --------------------------------------------------------------------------------------

/**
 * The one table with no editable cell. A log is the record of something that happened, and a log an
 * admin can rewrite is worth less than no log — so the only write here is delete, which exists to clear
 * the noise a testing session just produced rather than to manage storage.
 */
export default function LogsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const navigate = useNavigate();
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");
    const [severity, setSeverity] = useState("");
    const [method, setMethod] = useState("");

    const table = useResourceTable<TySystemLogRow, TyStats>({
        path: "logs",
        rowsKey: "logs",
        query: { search, method, severity: severity || (tab === "errors" ? "error" : "") },
    });

    const isEditable = canEdit("logs");

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.lists.logs.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "errors", label: t.lists.logs.statErrors, value: formatNumber(table.stats.errors, lang), tone: "bad" as const },
            { key: "rejected", label: t.lists.logs.statRejected, value: formatNumber(table.stats.rejected, lang), tone: "warn" as const },
            { key: "avg", label: t.lists.logs.statAvg, value: formatMs(table.stats.avgResponseTime, lang) },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TySystemLogRow>[]>(
        () => [
            { key: "method", label: t.cols.method, width: "90px", render: (row) => <span className="list-cell-strong">{row.method}</span> },
            {
                key: "route",
                label: t.cols.route,
                width: "minmax(180px, 1.8fr)",
                render: (row) => (
                    <span className="list-cell-mono" dir="ltr">
                        {row.route ?? row.url}
                    </span>
                ),
            },
            { key: "status", label: t.cols.status, width: "80px", align: "center", render: (row) => <span>{row.status}</span> },
            {
                key: "severity",
                label: t.cols.severity,
                width: "110px",
                render: (row) => <span data-severity={row.severity}>{t.enums.logSeverity[row.severity]}</span>,
            },
            {
                key: "responseTime",
                label: t.cols.responseTime,
                width: "90px",
                align: "end",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatMs(row.responseTime, lang)}</span>,
            },
            {
                key: "store",
                label: t.cols.store,
                width: "minmax(130px, 1fr)",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.store?.name ?? "—"}</span>,
            },
            {
                key: "createdAt",
                label: t.cols.created,
                width: "140px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDateTime(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang]
    );

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-log");
        addComp({
            name: "confirm-delete-log",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.lists.logs.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.lists.logs.tabAll },
                    { value: "errors", label: t.lists.logs.tabErrors },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.lists.logs.search }}
                activeFilterCount={[severity, method].filter(Boolean).length}
                onClearFilters={() => {
                    setSeverity("");
                    setMethod("");
                }}
                filters={
                    <>
                        <FilterSelect
                            label={t.cols.severity}
                            value={severity}
                            onChange={setSeverity}
                            options={SEVERITIES.map((one) => ({ value: one, label: t.enums.logSeverity[one] }))}
                        />
                        <FilterSelect
                            label={t.cols.method}
                            value={method}
                            onChange={setMethod}
                            options={METHODS.map((one) => ({ value: one, label: one }))}
                        />
                    </>
                }
            />

            <DataTable<TySystemLogRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                rowActions={(row) => (
                    <>
                        <button type="button" className="list-row-btn" onClick={() => navigate(`/logs/${row._id}`)} aria-label={t.table.preview}>
                            <img src="/icons/eye-opened-icon.svg" alt="" width={16} height={16} />
                        </button>
                        {isEditable && (
                            <button
                                type="button"
                                className="list-row-btn list-row-btn--danger"
                                onClick={() => confirmDelete([row._id], row.route ?? row.url)}
                                aria-label={t.table.deleteRow}
                            >
                                <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                            </button>
                        )}
                    </>
                )}
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
